/**
 * „Kam a s kým posíláte?" — countries, carriers and who buys (KAN-1473).
 * Moved verbatim out of ``OnboardingStart``. The carriers are handed in: they
 * depend on the picked countries, and which endpoint answers that is the
 * page's business.
 */

import { IngotIcon } from "@/ingot";
import { useTranslation } from "react-i18next";

import { cx } from "@/lib/utils";

import { DeliveryReview } from "./DeliveryReview";
import {
  fastestTransitDays,
  heaviestBandKg,
  regionName,
  type WizardCarrier,
  type WizardCatalog,
} from "./model";
import { PickCard } from "./PickCard";
import { stepHeading as heading } from "./StepHeading";
import { toggleIn as toggle, type WizardSelection } from "./useWizardSelection";

export function DeliveryStep({
  w,
  catalog,
  carriers,
  carriersLoading = false,
  lang,
  review,
}: {
  w: WizardSelection;
  catalog: WizardCatalog | null;
  carriers: WizardCarrier[];
  /** KAN-1497 — dokud odpověď nedorazila, prázdný seznam NENÍ „žádní
   *  dopravci". Bez tohohle rozlišení se na ~1 s vypsal verdikt
   *  „nemáme dopravce" i tenantovi, kterému endpoint vzápětí vrátil
   *  šest. */
  carriersLoading?: boolean;
  lang: string;
  /** KAN-1837 — jen admin průvodce: ceny pásem a rychlosti ke kontrole.
   *  Veřejný katalog landingu je nemá, ten se drží jmen dopravců. */
  review?: Omit<
    Parameters<typeof DeliveryReview>[0],
    | "carriers"
    | "carrierCodes"
    | "countryName"
    | "numbers"
    | "transitDays"
    | "heaviestKg"
    | "personalPickup"
    | "lang"
  >;
  }) {
  const { t } = useTranslation("admin");
  const {
    countryCodes,
    setCountryCodes,
    carrierCodes,
    setCarrierCodes,
    buyers,
    buyerOn,
    toggleBuyer,
    personalPickup,
    setPersonalPickup,
  } = w;
  const countryName = (code: string) => regionName(lang, code);
  // KAN-2015 / KAN-2019 — sekce kroku se číslují průběžně podle toho, které
  // jsou vidět (ceny dopravy a termíny jen v adminu a jen když je co ukázat).
  const showShipping =
    review !== undefined &&
    carriers.some(
      (c) => carrierCodes.includes(c.code) && (c.rates?.length ?? 0) > 0,
    );
  const showTiers = review !== undefined && review.tiers.length > 0;
  const order = [
    "countries",
    "carriers",
    ...(showShipping ? ["shipping"] : []),
    ...(showTiers ? ["tiers"] : []),
    "pickup",
    "buyers",
  ];
  const no = (section: string) => order.indexOf(section) + 1;
  const numbered = (section: string, title: string) =>
    t("onboardingStart.sectionNumbered", { n: no(section), title });
  const firstCountry = countryCodes[0];
  const transitDays = fastestTransitDays(
    carriers,
    carrierCodes,
    firstCountry,
    catalog?.countries.find((c) => c.country_code === firstCountry)?.extra_days ?? 0,
  );
  const heaviestKg = heaviestBandKg(carriers, carrierCodes);
  return (
    <>
      {heading(
        t("onboardingStart.q.delivery"),
        t("onboardingStart.sub.delivery"),
      )}
      <section className="opblock">
        <header className="opblock-head">
          <h3>{numbered("countries", t("onboardingStart.q.countries"))}</h3>
          <span
            className={cx("tag", countryCodes.length ? "tag-ok" : "tag-warn")}
          >
            {countryCodes.length
              ? t("onboardingStart.tags.countries", {
                  count: countryCodes.length,
                })
              : t("onboardingStart.tags.nothing")}
          </span>
        </header>
        <div className="opblock-body">
          <div
            className="pick-grid tight"
            role="group"
            aria-label={t("onboardingStart.q.countries")}
          >
            {(catalog?.countries ?? []).map((c) => (
              <PickCard
                key={c.country_code}
                on={countryCodes.includes(c.country_code)}
                onClick={() =>
                  setCountryCodes((current) =>
                    toggle(current, c.country_code),
                  )
                }
                title={countryName(c.country_code)}
                meta={c.country_code}
                testId={`onboarding-country-${c.country_code}`}
              />
            ))}
          </div>
        </div>
      </section>

      <section className="opblock">
        <header className="opblock-head">
          <h3>{numbered("carriers", t("onboardingStart.q.carriers"))}</h3>
          <span
            className={cx("tag", carrierCodes.length ? "tag-ok" : "tag-warn")}
          >
            {carrierCodes.length
              ? t("onboardingStart.tags.selected", {
                  count: carrierCodes.length,
                })
              : t("onboardingStart.tags.nothing")}
          </span>
        </header>
        <div className="opblock-body">
          {countryCodes.length === 0 ? (
            <div className="zero">
              <IngotIcon name="info" size={18} />
              <div>
                <b>{t("onboardingStart.carriers.pickTitle")}</b>
                <div className="t-small">
                  {t("onboardingStart.carriers.pickText")}
                </div>
              </div>
            </div>
          ) : carriersLoading ? (
            <div className="zero" data-testid="onboarding-carriers-loading">
              <IngotIcon name="info" size={18} />
              <div className="t-small">
                {t("onboardingStart.carriers.loading")}
              </div>
            </div>
          ) : carriers.length === 0 ? (
            <div className="zero" data-testid="onboarding-no-carriers">
              <IngotIcon name="info" size={18} />
              <div className="t-small">
                {t("onboardingStart.carriers.empty")}
              </div>
            </div>
          ) : (
            <>
              <p className="q-sub" style={{ marginBottom: 12 }}>
                {t("onboardingStart.carriers.lead")}
              </p>
              <div
                className="pick-grid"
                role="group"
                aria-label={t("onboardingStart.q.carriers")}
              >
                {carriers.map((c) => (
                  <PickCard
                    key={c.code}
                    on={carrierCodes.includes(c.code)}
                    onClick={() =>
                      setCarrierCodes((current) => toggle(current, c.code))
                    }
                    title={c.name}
                    sub={c.delivery_method ?? undefined}
                    testId={`onboarding-carrier-${c.code}`}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      {review ? (
        <DeliveryReview
          {...review}
          carriers={carriers}
          carrierCodes={carrierCodes}
          countryName={countryName}
          numbers={{ shipping: no("shipping"), tiers: no("tiers") }}
          transitDays={transitDays}
          heaviestKg={heaviestKg}
          personalPickup={personalPickup}
          lang={lang}
        />
      ) : null}

      <section className="opblock">
        <header className="opblock-head">
          <h3>{numbered("pickup", t("onboardingStart.q.pickup"))}</h3>
          <span className={cx("tag", personalPickup ? "tag-ok" : "tag-warn")}>
            {personalPickup
              ? t("onboardingStart.tags.selected", { count: 1 })
              : t("onboardingStart.tags.nothing")}
          </span>
        </header>
        <div className="opblock-body">
          <p className="q-sub" style={{ marginBottom: 12 }}>
            {t("onboardingStart.pickup.lead")}
            {/* Jen v adminu — veřejné zkoušení (PlatformTryout) admin nemá. */}
            {review ? (
              <>
                {" "}
                <a
                  href={`/${lang}/admin/shipping`}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="onboarding-pickup-shipping-link"
                >
                  {t("onboardingStart.pickup.shippingLink")}
                </a>
              </>
            ) : null}
          </p>
          <div
            className="pick-grid"
            role="group"
            aria-label={t("onboardingStart.q.pickup")}
          >
            <PickCard
              on={personalPickup}
              onClick={() => setPersonalPickup((current) => !current)}
              title={t("onboardingStart.pickup.title")}
              sub={t("onboardingStart.pickup.note")}
              testId="onboarding-personal-pickup"
            />
          </div>
        </div>
      </section>

      <section className="opblock">
        <header className="opblock-head">
          <h3>{numbered("buyers", t("onboardingStart.q.buyers"))}</h3>
          <span className={cx("tag", buyers ? "tag-ok" : "tag-warn")}>
            {buyers
              ? t("onboardingStart.tags.selected", {
                  count: buyers === "both" ? 2 : 1,
                })
              : t("onboardingStart.tags.nothing")}
          </span>
        </header>
        <div className="opblock-body">
          <p className="q-sub" style={{ marginBottom: 12 }}>
            {t("onboardingStart.buyers.lead")}
          </p>
          <div
            className="pick-grid"
            role="group"
            aria-label={t("onboardingStart.q.buyers")}
          >
            {(["b2b", "b2c"] as const).map((side) => (
              <PickCard
                key={side}
                on={buyerOn(side)}
                onClick={() => toggleBuyer(side)}
                title={t(`onboardingStart.buyers.${side}`)}
                sub={t(`onboardingStart.buyers.${side}Note`)}
                testId={`onboarding-buyer-${side}`}
              />
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
